import { useMetrics, useReports } from '@sail/observability';
import { getEnvironment } from '@sail/utils';
import { useCallback, useMemo } from 'react';
import { NavigationError } from '../../errors';
import { useNavigationConfig } from '../../internal/config/NavigationConfigContext';
import urlToRoutes from '../../internal/urlToRoutes';
import useRouteToUrl from '../../internal/useRouteToUrl';
import createRoute from '../../public/createRoute/createRoute';
import useHref from '../../public/useHref/useHref';
import useRoutes from '../../public/useRoutes/useRoutes';
let visitedHref;
function throwHrefError(error, href) {
    const errorMessage = `No route matched for "${href}", is there a catch-all?`;
    if (getEnvironment() !== 'production') {
        error(errorMessage, {});
    }
    throw new NavigationError(errorMessage);
}
/**
 * @see https://sail.stripe.me/apis/navigation/useRoute
 */ export default function useRoute() {
    const { warning, error } = useReports();
    const { increment } = useMetrics();
    const config = useNavigationConfig();
    const [href, setHref] = useHref();
    const routeToUrl = useRouteToUrl();
    const routes = useRoutes();
    const routeName = useMemo(()=>getFirstRouteName(routes), [
        routes
    ]);
    if (!routeName) {
        throwHrefError(error, href);
    }
    const route = routes[routeName];
    const currentRoute = route ? createRoute(route) : route;
    if (!currentRoute) {
        throwHrefError(error, href);
    }
    if (visitedHref !== href) {
        // eslint-disable-next-line react-hooks/globals
        visitedHref = href;
        switch(routeName){
            case 'CATCH_ALL':
                if (getEnvironment() !== 'production') {
                    warning('The URL does not match any registered route');
                }
                increment('stripelets.navigation.cannot_match_route');
                break;
            case 'INVALID_URL':
                increment('stripelets.navigation.invalid_url');
                warning('The Invalid URL is detected');
                break;
        }
    }
    const setRoute = useCallback((newRoute, replace = false)=>{
        if (typeof newRoute === 'function') {
            return setHref((prevHref)=>{
                const routes = urlToRoutes(config, prevHref);
                const routeName = getFirstRouteName(routes);
                if (!routeName) {
                    throwHrefError(error, prevHref);
                }
                const route = routes[routeName];
                const prevRoute = createRoute(route);
                return routeToUrl(newRoute(prevRoute), prevRoute);
            }, replace);
        }
        return setHref(routeToUrl(newRoute, currentRoute), replace);
    }, [
        setHref,
        currentRoute,
        routeToUrl,
        config,
        error
    ]);
    return [
        currentRoute,
        setRoute
    ];
}
function getFirstRouteName(routes) {
    for(const name in routes){
        if (routes.hasOwnProperty(name)) {
            return name;
        }
    }
    return undefined;
}

//# sourceMappingURL=useRoute.js.map